// Lab runtime: owns one learning unit's live state and enforces its
// validation rules. The UI only renders what the runtime says.
//
// Persisted state (store key lab:<domain>.<unit>):
// {
//   stage: index of the current stage,
//   net: network object (see engine/net/model.js),
//   sessions: { deviceId: console session state },
//   scroll: { deviceId: [ {text, cls} ] },
//   evidence: { stageId: { evidenceId: true } },
//   widgets: { widgetId: { done, data } },
//   hints: { stageId: numberRevealed },
//   verify: { stageId: { ok, results:[...] } },
//   entered: { stageId: true },    // onEnter hooks (fault injection) run once
//   log: [ events ],               // command + action history (capped)
//   data: {},                      // unit-specific scratch (random faults, answers)
//   completed: bool,
//   seed: number,                  // seeds rt.random() (random faults) so the server can replay the lab
//   journal: [ entries ],          // every learner input, in order — replayed by src/core/labVerify.js
// }
//
// Journal entries (server-verified progress, see src/core/labVerify.js):
//   { k:'x', d, l }      console command on device d
//   { k:'a', n, p }      plain learner action (inspect a cable, read a section, …)
//   { k:'o', n, p }      a change the learner made outside the console (adapter GUI, reseat, replace cable)
//   { k:'w', id, data }  an activity widget was completed with this data
//   { k:'h' }            a coach hint was revealed
//   { k:'v' }            VERIFY was pressed

import { store } from './store.js';
import { unitKey } from './progress.js';
import { IosSession, banner } from '../engine/ios/cli.js';
import { WinShell } from '../engine/pc/winshell.js';
import { isHost, dev } from '../engine/net/sim.js';
import { clone } from '../engine/net/model.js';
import { HINT_XP_RULE } from './xp.js';
import { applyOp } from './labOps.js';

const MAX_LOG = 400;
const MAX_SCROLL = 500;
export const MAX_JOURNAL = 4000;

const newSeed = () => {
  try { const a = new Uint32Array(1); globalThis.crypto.getRandomValues(a); return a[0] || 1; } catch { return (Math.floor(Math.random() * 0xFFFFFFFF) >>> 0) || 1; }
};
const cloneData = (v) => JSON.parse(JSON.stringify(v ?? {}));

export class LabRuntime {
  constructor(domainId, unit, progress, { st = progress?.store || store } = {}) {
    this.domainId = domainId;
    this.unit = unit;
    this.progress = progress;
    this.st = st;
    this.key = `lab:${unitKey(domainId, unit.id)}`;
    this.listeners = new Set();
    this.shells = {};
    const saved = st.get(this.key);
    this.isFresh = !(saved && saved.v === (unit.version || 1));
    this.state = this.isFresh ? this.fresh() : saved;
  }

  fresh() {
    const s = {
      v: this.unit.version || 1, stage: 0, net: this.unit.network ? this.unit.network() : null,
      sessions: {}, scroll: {}, evidence: {}, widgets: {}, hints: {}, verify: {}, entered: {},
      log: [], data: {}, completed: false, startedAt: Date.now(),
      seed: newSeed(), rng: 0, journal: [],
    };
    return s;
  }
  /** Called after construction on a brand-new lab (not on resume). */
  init() {
    if (this.isFresh) {
      this.isFresh = false;
      if (this.unit.setup) {
        this.unit.setup(this);   // e.g. roll which faults this attempt contains
        this.enterStage(false);
        this.save();             // the seed must survive a page reload
      } else this.enterStage(false);
    }
    return this;
  }

  // ------------------------------------------------------------ plumbing
  on(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  emit(type, payload) { for (const f of this.listeners) f(type, payload); }
  save() {
    // Persist only when the lab has been touched; opening alone does not create
    // "in progress" noise (but its random fault seed must stay stable).
    this.st.set(this.key, this.state);
  }

  // ------------------------------------------------------------ replayable inputs
  /** Append one learner input to the journal (see the header). Labs started before journals existed have none. */
  journalize(entry) {
    const j = this.state.journal;
    if (!Array.isArray(j)) return;
    if (j.length >= MAX_JOURNAL) { this.state.journalOverflow = true; return; }
    j.push(entry);
  }
  /** Deterministic random number in [0, 1) for this attempt (mulberry32 over the lab seed). */
  random() {
    let a = ((this.state.seed >>> 0) + Math.imul(0x6D2B79F5, (this.state.rng = (this.state.rng || 0) + 1))) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  /** Widget definition by id, from any stage of this unit. */
  widgetDef(id) {
    for (const st of this.stages) for (const w of st.widgets || []) if (w.id === id) return w;
    return null;
  }

  get stages() { return this.unit.stages; }
  get stage() { return this.stages[this.state.stage]; }
  get net() { return this.state.net; }
  get data() { return this.state.data; }

  // ------------------------------------------------------------ consoles
  shell(id) {
    if (!this.shells[id]) {
      const d = dev(this.net, id);
      const s = isHost(d) ? new WinShell(this.net, id, this.state.sessions[id]) : new IosSession(this.net, id, this.state.sessions[id]);
      this.state.sessions[id] = s.state;
      this.shells[id] = s;
    } else this.shells[id].bind(this.net);
    return this.shells[id];
  }
  consoleDevices() {
    return this.stage?.devices || this.unit.devices || [];
  }
  scrollback(id) {
    if (!this.state.scroll[id]) {
      const d = dev(this.net, id);
      this.state.scroll[id] = isHost(d)
        ? [{ text: 'Windows PowerShell' }, { text: 'Copyright (C) Microsoft Corporation. All rights reserved.' }, { text: '' }, { text: 'Install the latest PowerShell for new features and improvements! https://aka.ms/PSWindows', cls: 'dim' }, { text: '' }]
        : banner(d).map((text) => ({ text }));
    }
    return this.state.scroll[id];
  }
  appendScroll(id, lines) {
    const s = this.scrollback(id);
    s.push(...lines.map((l) => ({ text: l.text, cls: l.cls })));
    if (s.length > MAX_SCROLL) s.splice(0, s.length - MAX_SCROLL);
  }
  clearScroll(id) { this.state.scroll[id] = []; }

  /** Run a console command. Returns the shell result (lines + event). */
  exec(id, line) {
    this.journalize({ k: 'x', d: id, l: String(line) });
    const sh = this.shell(id);
    const r = sh.exec(line);
    const ev = { type: 'cmd', t: Date.now(), stage: this.stage?.id, ...r.event };
    this.record(ev);
    return r;
  }

  /** Non-console learner actions (reseat cable, submit a form, ...). */
  action(name, payload = {}) {
    this.journalize({ k: 'a', n: name, p: cloneData(payload) });
    return this.act(name, payload);
  }
  /** Record an action without journaling it (runtime-internal: verify, hint, operations). */
  act(name, payload = {}) {
    const ev = { type: 'action', name, t: Date.now(), stage: this.stage?.id, ...payload };
    this.record(ev);
    return ev;
  }

  record(ev) {
    this.state.log.push(stripOutput(ev));
    if (this.state.log.length > MAX_LOG) this.state.log.shift();
    this.collectEvidence(ev);
    this.save();
    this.emit('event', ev);
  }

  /**
   * A change the learner makes outside the console (Windows adapter dialog, reseat or replace a
   * cable). Applied by src/core/labOps.js so the server replays exactly the same change.
   * Returns false (and changes nothing) when the operation is not valid here.
   */
  op(name, payload = {}) {
    const ev = applyOp(this, name, payload);
    if (!ev) return false;
    this.journalize({ k: 'o', n: name, p: cloneData(payload) });
    this.act(name, ev);
    return true;
  }

  // ------------------------------------------------------------ evidence
  collectEvidence(ev) {
    const st = this.stage;
    if (!st?.evidence || this.state.completed) return;
    const got = (this.state.evidence[st.id] = this.state.evidence[st.id] || {});
    for (const e of st.evidence) {
      if (got[e.id]) continue;
      try {
        if (e.match(ev, this)) { got[e.id] = true; this.emit('evidence', e); }
      } catch (err) { console.warn('evidence rule failed', e.id, err); }
    }
  }
  evidenceStatus(stage = this.stage) {
    const got = this.state.evidence[stage.id] || {};
    return (stage.evidence || []).map((e) => ({ ...e, done: !!got[e.id] }));
  }

  // ------------------------------------------------------------ widgets
  widget(id) {
    return (this.state.widgets[id] = this.state.widgets[id] || { done: false, data: {} });
  }
  widgetDone(id) {
    if (!this.widget(id).done) this.journalize(this.widgetDef(id)?.selfCompleting ? { k: 'w', id } : { k: 'w', id, data: cloneData(this.widget(id).data) });
    this.widget(id).done = true;
    this.save();
    this.emit('widget', id);
  }

  // ------------------------------------------------------------ hints
  revealHint() {
    const st = this.stage;
    const n = (this.state.hints[st.id] || 0) + 1;
    this.state.hints[st.id] = Math.min(n, (st.hints || []).length);
    this.journalize({ k: 'h' });
    this.act('hint', { n: this.state.hints[st.id] });
    return this.state.hints[st.id];
  }
  hintsShown(stage = this.stage) { return this.state.hints[stage.id] || 0; }

  // ------------------------------------------------------------ validation
  /** What still blocks the current stage (UI shows this list). */
  blockers(stage = this.stage) {
    const b = [];
    for (const w of stage.widgets || []) if (w.required !== false && !this.widget(w.id).done) b.push({ kind: 'widget', text: w.blocker || 'Complete the activity above' });
    for (const e of this.evidenceStatus(stage)) if (!e.done) b.push({ kind: 'evidence', text: e.text });
    return b;
  }

  /**
   * The learner presses VERIFY. Evidence/widgets must be complete; then each
   * task's check() is evaluated against live state. Only a full pass advances.
   */
  verify() {
    this.journalize({ k: 'v' });
    const st = this.stage;
    const blockers = this.blockers(st);
    if (blockers.length) {
      const res = { ok: false, blocked: true, blockers };
      this.emit('verify', res);
      return res;
    }
    const results = (st.tasks || []).map((t) => {
      let ok = false;
      try { ok = !!t.check(this); } catch (err) { console.warn('check failed', t.id, err); }
      return { id: t.id, text: t.text, ok, fail: ok ? null : (typeof t.fail === 'function' ? t.fail(this) : t.fail) };
    });
    const ok = results.every((r) => r.ok);
    this.state.verify[st.id] = { ok, results, t: Date.now() };
    this.act('verify', { ok, stageId: st.id });
    const res = { ok, results };
    if (ok) this.advance();
    this.emit('verify', res);
    return res;
  }

  /**
   * Move to the next stage. `bypass` is used ONLY by Admin / Development Test
   * Mode ("Pass stage (skip checks)"): it records that the stage was not
   * validated, and a unit finished with any bypassed stage is marked
   * {test, bypass, validated:false}. Learner validation never passes `bypass`.
   */
  advance({ bypass = false } = {}) {
    if (bypass) this.markBypass(this.stage.id);
    if (this.state.stage < this.stages.length - 1) {
      this.state.stage++;
      this.enterStage(true);
    } else {
      this.state.completed = true;
      this.state.completedAt = Date.now();
      const hints = Object.values(this.state.hints).reduce((a, b) => a + b, 0);
      this.progress.markComplete(this.domainId, this.unit.id, this.wasBypassed() ? { hints, test: true, bypass: true, validated: false } : { hints, xpRule: HINT_XP_RULE });
      this.save();
      this.emit('complete');
    }
  }

  /** Admin test bypass bookkeeping (lives in the lab state of the admin sandbox). */
  markBypass(what) {
    this.state.bypass = { ...(this.state.bypass || {}), [what]: Date.now() };
    this.save();
  }
  wasBypassed() { return !!this.state.bypass && Object.keys(this.state.bypass).length > 0; }

  enterStage(persist = true) {
    const st = this.stage;
    if (st && !this.state.entered[st.id]) {
      this.state.entered[st.id] = true;
      if (st.onEnter) st.onEnter(this);
    }
    if (persist) this.save();
    this.emit('stage', st);
  }

  // ------------------------------------------------------------ helpers for checks
  /** Run fn against a throwaway copy of the network (no side effects). */
  probe(fn) { return fn(clone(this.net)); }
  /** Events in the log matching a predicate (optionally only this stage). */
  events(pred, { stageOnly = false } = {}) {
    return this.state.log.filter((e) => (!stageOnly || e.stage === this.stage.id) && pred(e));
  }
}

function stripOutput(ev) {
  // Keep a short output excerpt only — full output lives in scrollback.
  const { output, ...rest } = ev;
  return { ...rest, output: output ? output.slice(0, 1500) : '' };
}
